/**
 * Dashboard.tsx — Workspace home (Ingest stage default view).
 * Ported from prototype stage-ingest.jsx `Dashboard`. Greeting personalized
 * to the real Auth0 user. The recent-projects table AND the stat cards are LIVE:
 * the table reads the tenant's projects via useProjects, and the stat cards read
 * org-wide aggregates via useDashboardStats (GET /v1/projects/stats) — both from
 * tender-api (lib/tenderApi.ts). The one exception is the "Margine medio" card,
 * a deliberate "In arrivo" placeholder until the Strategia/Fornitori domain is
 * built (no margin data source exists yet).
 */
import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { I } from "../../components/icons";
import { DropdownMenu } from "../../components/ui/DropdownMenu";
import { apiErrorMessage } from "../../lib/api";
import { type DashboardStat } from "../../lib/mock";
import {
  STATUS_LABEL,
  useDashboardStats,
  useDeleteProject,
  useProjects,
  type DashboardStats,
  type TenderProject,
  type TenderProjectStatus,
} from "../../lib/tenderApi";

function firstName(name?: string, email?: string): string {
  const src = (name && !name.includes("@") ? name : email?.split("@")[0]) ?? "";
  const first = src.split(/[\s._-]+/).filter(Boolean)[0] ?? "";
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "";
}

const STATUS_PILL: Record<TenderProjectStatus, string> = {
  ingest: "pill-brand",
  audit: "pill-warn",
  strategy: "pill-info",
  completed: "pill-ok",
  archived: "pill",
};

/** Compact € formatting for the CME total — "€ 8,75M", or "—" when not yet analyzed. */
function formatCmeTotal(value: number | null): string {
  if (value == null) return "—";
  if (value >= 1_000_000) {
    const m = (value / 1_000_000).toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `€ ${m}M`;
  }
  return `€ ${Math.round(value).toLocaleString("it-IT")}`;
}

/**
 * The 4 dashboard stat cards, built from live org-wide aggregates. `s` is the
 * loaded stats (undefined while loading/error → cards render a "…" / "—"
 * placeholder). The "Margine medio" card stays a pointer, not a number: the
 * margin is computed per project in Strategia (and is still provisional — the
 * exact formula is pending confirmation), so there is no org-wide average to
 * show without fabricating one.
 */
function buildStatCards(s: DashboardStats | undefined, isError: boolean): DashboardStat[] {
  const num = (v: number): string => (s ? v.toLocaleString("it-IT") : isError ? "—" : "…");
  const analyzedNote = s
    ? `${s.analyzedProjects} ${s.analyzedProjects === 1 ? "analizzato" : "analizzati"}`
    : "";
  return [
    {
      label: "Progetti attivi",
      value: s ? String(s.activeProjects) : isError ? "—" : "…",
      delta: analyzedNote,
    },
    {
      label: "Volume CME totale",
      value: s ? formatCmeTotal(s.totalCmeValue) : isError ? "—" : "…",
      delta: s
        ? `su ${s.analyzedProjects} ${s.analyzedProjects === 1 ? "progetto" : "progetti"}`
        : "",
    },
    {
      label: "Margine medio",
      value: "—",
      delta: "Per progetto in Strategia",
    },
    {
      label: "Discrepanze aperte",
      value: num(s?.openDiscrepancies ?? 0),
      delta: s ? `${s.criticalDiscrepancies} critiche` : "",
      warn: !!s && s.criticalDiscrepancies > 0,
    },
  ];
}

function StatCard({ stat }: { stat: DashboardStat }): JSX.Element {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", fontWeight: 500 }}>{stat.label}</div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 600,
          marginTop: 4,
          letterSpacing: "-0.02em",
          fontFamily: "var(--font-mono)",
        }}
      >
        {stat.value}
      </div>
      <div
        style={{
          fontSize: 11.5,
          marginTop: 6,
          color: stat.warn
            ? "var(--c-warn)"
            : stat.trend === "up"
              ? "var(--c-ok)"
              : "var(--c-ink-3)",
        }}
      >
        {stat.trend === "up" && "↗ "}
        {stat.trend === "down" && "↘ "}
        {stat.delta}
      </div>
    </div>
  );
}

function ProjectRow({
  p,
  onOpen,
  onDelete,
}: {
  p: TenderProject;
  onOpen: () => void;
  onDelete: () => void;
}): JSX.Element {
  return (
    <tr onClick={onOpen} style={{ cursor: "pointer" }}>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "var(--c-brand-soft)",
              color: "var(--c-brand-ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {p.name
              .split(" ")
              .slice(0, 2)
              .map((w) => w[0])
              .join("")}
          </div>
          <div>
            <div style={{ fontWeight: 500, color: "var(--c-ink)" }}>{p.name}</div>
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 2 }}>
              <I.Pin
                size={10}
                style={{ display: "inline", verticalAlign: "-2px", marginRight: 3 }}
              />
              {p.city ?? "—"}
            </div>
          </div>
        </div>
      </td>
      <td>
        <span className={`pill ${STATUS_PILL[p.status]}`}>{p.statusLabel}</span>
      </td>
      <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-2)" }}>
        {p.surfaceM2 != null ? `${p.surfaceM2.toLocaleString("it-IT")} m²` : "—"}
      </td>
      <td className="t-num" style={{ textAlign: "right", fontWeight: 500 }}>
        {formatCmeTotal(p.totalValue)}
      </td>
      <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-2)" }}>
        {p.documentCount}
      </td>
      <td style={{ color: "var(--c-ink-3)", fontSize: 12 }}>{p.updatedLabel}</td>
      <td onClick={(e) => e.stopPropagation()}>
        <DropdownMenu
          align="right"
          width={160}
          items={[
            { icon: I.External, label: "Apri", onClick: onOpen },
            { icon: I.Trash, label: "Elimina", danger: true, onClick: onDelete },
          ]}
          renderTrigger={(toggle) => (
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
            >
              <I.More size={14} />
            </button>
          )}
        />
      </td>
    </tr>
  );
}

export function Dashboard({ onNew }: { onNew: () => void }): JSX.Element {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const greeting = firstName(user?.name, user?.email);

  const [statusFilter, setStatusFilter] = useState<TenderProjectStatus | "all">("all");
  const projectsQuery = useProjects({
    status: statusFilter === "all" ? undefined : statusFilter,
  });
  const statsQuery = useDashboardStats();
  const statCards = buildStatCards(statsQuery.data, statsQuery.isError);
  const deleteProject = useDeleteProject();
  const projects = projectsQuery.data ?? [];
  const STATUS_OPTIONS: (TenderProjectStatus | "all")[] = [
    "all",
    "ingest",
    "audit",
    "strategy",
    "completed",
  ];

  function handleDelete(p: TenderProject): void {
    if (window.confirm(`Eliminare il progetto "${p.name}"? Verrà archiviato.`)) {
      deleteProject.mutate(p.id);
    }
  }

  return (
    <div style={{ padding: "28px 36px 60px", maxWidth: 1280, width: "100%", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 28,
        }}
      >
        <div>
          <div className="t-eyebrow" style={{ marginBottom: 6 }}>
            Workspace
          </div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              margin: 0,
              color: "var(--c-ink)",
            }}
          >
            Buongiorno{greeting ? `, ${greeting}` : ""}.
          </h1>
          <p style={{ marginTop: 6, color: "var(--c-ink-3)", fontSize: 14 }}>
            {projectsQuery.isLoading
              ? "Caricamento dei progetti…"
              : projects.length === 0
                ? "Nessun progetto ancora. Crea il primo per iniziare."
                : `Hai ${projects.length} ${projects.length === 1 ? "progetto" : "progetti"}${
                    statusFilter === "all" ? "" : " con questo stato"
                  }.`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <DropdownMenu
            align="right"
            width={320}
            header="Apri un progetto"
            items={projects.map((p) => ({
              icon: I.Folder,
              label: p.name,
              onClick: () => navigate(`/audit/${p.id}`),
            }))}
            renderTrigger={(toggle) => (
              <button className="btn" onClick={toggle}>
                <I.Folder size={14} /> Apri progetto
              </button>
            )}
          />
          <button className="btn btn-primary" onClick={onNew}>
            <I.Plus size={14} /> Nuovo progetto
          </button>
        </div>
      </div>

      {/* Stat row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
          marginBottom: 28,
        }}
      >
        {statCards.map((s) => (
          <StatCard key={s.label} stat={s} />
        ))}
      </div>

      {/* Recent projects */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Progetti recenti</div>
            <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 2 }}>
              Clicca per aprire
            </div>
          </div>
          <DropdownMenu
            align="right"
            width={190}
            header="Filtra per stato"
            items={STATUS_OPTIONS.map((s) => ({
              label: s === "all" ? "Tutti gli stati" : STATUS_LABEL[s],
              active: statusFilter === s,
              onClick: () => setStatusFilter(s),
            }))}
            renderTrigger={(toggle) => (
              <button className="btn btn-ghost btn-sm" onClick={toggle}>
                <I.Filter size={13} /> Filtra{statusFilter !== "all" ? " (1)" : ""}
              </button>
            )}
          />
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: "38%" }}>Progetto</th>
              <th>Stato</th>
              <th style={{ textAlign: "right" }}>Superficie</th>
              <th style={{ textAlign: "right" }}>Valore CME</th>
              <th style={{ textAlign: "right" }}>Documenti</th>
              <th style={{ width: 110 }}>Aggiornato</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {projectsQuery.isLoading && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}
                >
                  Caricamento…
                </td>
              </tr>
            )}
            {projectsQuery.isError && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--c-err)" }}>
                  {apiErrorMessage(projectsQuery.error)}
                </td>
              </tr>
            )}
            {projectsQuery.isSuccess &&
              projects.map((p) => (
                <ProjectRow
                  key={p.id}
                  p={p}
                  onOpen={() => navigate(`/audit/${p.id}`)}
                  onDelete={() => handleDelete(p)}
                />
              ))}
            {projectsQuery.isSuccess && projects.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}
                >
                  {statusFilter === "all"
                    ? "Nessun progetto ancora. Crea il primo con “Nuovo progetto”."
                    : "Nessun progetto con questo stato."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
